import React, { useState } from 'react';

/**
 * Component to handle chat functionality with a selected connection
 * @param {Object} props
 * @param {Object} props.selectedConnection - The currently selected connection
 * @param {Function} props.onClose - Function to handle closing the panel
 * @param {Boolean} props.isOnPhone - Whether the user is on a mobile device
 * @param {Object} props.linkedInProfile - The logged-in user's LinkedIn profile
 * @returns {JSX.Element} Chat panel component
 */
const ConnectionChatPanel = ({ selectedConnection, onClose, isOnPhone, linkedInProfile }) => {
  const [chatMessages, setChatMessages] = useState({});
  const [currentMessage, setCurrentMessage] = useState('');

  // Handle sending a message
  const handleSendMessage = () => {
    if (!currentMessage.trim() || !selectedConnection) return;

    // Update chat messages for selected connection
    setChatMessages(prev => ({
      ...prev,
      [selectedConnection.id]: [
        ...(prev[selectedConnection.id] || []),
        { sender: 'user', text: currentMessage, timestamp: new Date() }
      ]
    }));

    setCurrentMessage('');

    // Simulate response after a short delay
    setTimeout(() => {
      setChatMessages(prev => ({
        ...prev,
        [selectedConnection.id]: [
          ...(prev[selectedConnection.id] || []),
          {
            sender: 'connection',
            text: `This is a simulated response from ${selectedConnection.name}`,
            timestamp: new Date()
          }
        ]
      }));
    }, 1000);
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {selectedConnection ? (
        <>
          {/* Chat messages */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50 dark:bg-[#1e1e1e]">
            <div className="space-y-4">
              {(chatMessages[selectedConnection.id] || []).map((message, idx) => (
                <div
                  key={idx}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md p-3 rounded-lg ${
                      message.sender === 'user'
                        ? 'bg-blue-600 text-white rounded-br-none'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-none border dark:border-gray-700'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <p className={`text-xs mt-1 ${
                      message.sender === 'user' ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {(chatMessages[selectedConnection.id] || []).length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              )}
            </div>
          </div>

        </>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-[#1e1e1e]">
          <div className="text-center p-6">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No conversation selected</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Choose a connection from the list to start chatting
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionChatPanel;